import { Settings } from './Settings';
import { SpectreResponse } from './SpectreResponse';

function createErrorResponse(status: number, statusText: string, errorMessage: string) {
    return new Response(JSON.stringify({ error: errorMessage }), {
        status,
        statusText,
        headers: { "Content-Type": "application/json" },
    });
}

export async function fetch_with_timeout(url: string, resource: any, timeout: number = 10000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    resource.signal = controller.signal;
    resource.headers["Accept"] = "application/json";
    
    try {
        const response = await fetch(url, resource);
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        
        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                return createErrorResponse(408, "Request Timeout", `Request timed out after ${timeout}ms`);
            }
            return createErrorResponse(503, "Service Unavailable", error.message);
        }
        
        return createErrorResponse(500, "Unknown Error", String(error));
    }
}

export class IConnection {
    type(): string { return ""; }
    async file(name: string): Promise<string> { return ""; }
    async pendingFile(name: string, directory: string = "nextscan"): Promise<string> {
        return this.file(`${directory}/${name}`);
    }
    async imageThumbnail(name: string): Promise<string> {
        return this.file(`${name + "?thumbnail&width=480&height=270"}`)
    }
    async pendingImageThumbnail(name: string, directory: string = "nextscan"): Promise<string> {
        return this.pendingFile(name, directory);
    }
    async get_impl(cmd: string, timeout: number, headers: HeadersInit = {}): Promise<Response> { throw Error(); }
    async get(cmd: string, timeout: number = 5000, headers: HeadersInit = {}): Promise<SpectreResponse> {
        let result: Promise<SpectreResponse> = this.get_impl(cmd, timeout, headers)
            .then(response => {
                return response.json();
            })
            .catch((error) => {
                return { error: error instanceof Error ? error.message : "Unknown error" };
            })
        return result;
    }
    async post_impl(cmd: JSON, timeout: number): Promise<Response> { throw Error(); }
    async post(cmd: JSON, timeout: number = 50000): Promise<SpectreResponse> {
        let result: Promise<SpectreResponse> = this.post_impl(cmd, timeout)
            .then(response => {
                return response.json();
            })
            .catch((error) => {
                return { error: error instanceof Error ? error.message : "Unknown error" };
            })
        return result;
    }
    async post_form_impl(cmd: string, formData: FormData, timeout: number, headers: HeadersInit = {}): Promise<Response> { throw Error(); }
    async postForm(cmd: string, formData: FormData, timeout: number = 50000, headers: HeadersInit = {}): Promise<SpectreResponse> {
        let result: Promise<SpectreResponse> = this.post_form_impl(cmd, formData, timeout, headers)
            .then(response => {
                return response.json();
            })
            .catch((error) => {
                return { error: error instanceof Error ? error.message : "Unknown error" };
            })
        return result;
    }

    async ping(machine: any): Promise<boolean> {
        try {
            return await this.get("ping", 5000)
                .then((response: any) => {
                    if (response && response?.['Responses']?.['Ping']?.['code'] === '0') {
                        return true;
                    } else {
                        return false;
                    }
                })
        }
        catch {
            return false;
        }
    }

    async testConnection(machine: any): Promise<boolean> {
        try {
            return await this.get("ping+version", 2500)
                .then((response: any) => {
                    if (response && response?.['Responses']?.['Ping']?.['code'] === '0') {
                        if (response?.['Responses']?.['Version']?.['code'] === '0') {
                            machine.version = response?.['Responses']?.['Version']?.['value']
                            return true;
                        } else {
                            return false;
                        }
                    } else {
                        return false;
                    }
                })
        } catch (err) {
            return false;
        }
    }

    async calibrate(phase: string): Promise<SpectreResponse> {
        return this.get(`calibrate?Phase=${phase}`, 20000)
    }
}

export class RemoteConnection extends IConnection {
    ip: string
    protocol: string
    constructor(machine: any) {
        super();
        this.ip = this.convertIpAddressToSpectreCore(machine.publicIP)
        this.protocol = "https"
    }
    type(): string { return "Remote"; }
    // ***** NOTE - there is a double slash in front of file - for some reason images won't return from the machien at the moment
    async file(name: string): Promise<string> { return `${this.protocol}://${this.ip}/file/${name}` }
    async pendingFile(name: string, directory: string = "nextscan"): Promise<string> {
        return `${this.protocol}://${this.ip}/file/${directory}/${name}`;
    }

    async get_impl(cmd: string, timeout: number, headers: HeadersInit = {}): Promise<Response> {
        let target_ip = `${this.protocol}://${this.ip}/${cmd}`
        console.log("FETCH: ", target_ip)

        return fetch_with_timeout(`${this.protocol}://${this.ip}/${cmd}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...headers
            },
        }, timeout)
    }

    async post_impl(cmd: JSON, timeout: number): Promise<Response> {
        let target_ip = `${this.protocol}://${this.ip}`;
        console.log("POST FETCH: ", target_ip);
        return fetch_with_timeout(`${this.protocol}://${this.ip}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json', // Set content type to JSON
            },
            body: JSON.stringify(
                cmd
            ),
        }, timeout)
    }

    async post_form_impl(cmd: string, formData: FormData, timeout: number, headers: HeadersInit = {}): Promise<Response> {
        let target_ip = `${this.protocol}://${this.ip}/${cmd}`;
        console.log("FORM POST FETCH: ", target_ip);
        return fetch_with_timeout(target_ip, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                ...headers
            },
            body: formData,
        }, timeout)
    }

    convertIpAddressToSpectreCore(ipAddress: string) {
        // Remove the port number from the IP address
        const [address, port] = ipAddress.split(':');

        // Replace periods with hyphens and append the domain name
        const convertedAddress = address.replace(/\./g, '-') + '.spectrecore.com';

        // Combine the converted address with the port number
        const convertedIpAddress = convertedAddress + ':' + port;

        return convertedIpAddress;
    }
}

export class ProxyConnection extends IConnection {
    ip: string
    cookies: any
    constructor(machine: any, cookies: any) {
        super();
        this.ip = `https://proxy.spectre-licensing.com/api/${machine.machineName.replace(/ /g, '%20')}`;
        this.cookies = cookies
    }
    type(): string { return "Proxy" }

    updateMachineName(newName: string) {
        this.ip = `https://proxy.spectre-licensing.com/api/${newName.replace(/ /g, '%20')}`;
    }

    async file(name: string): Promise<string> {
        return this.get(`fileprep/${name}`, 5000)
            .then((data) => {
                return `https://proxy.spectre-licensing.com/api/fileresolve/${data}`
            })
    }

    async pendingFile(name: string, directory: string = "nextscan"): Promise<string> {
        return this.file(`${directory}/${name}`);
    }

    async imageThumbnail(name: string): Promise<string> {
        return this.get(`fileprep/${name}`, 5000)
            .then((data) => {
                return `https://proxy.spectre-licensing.com/api/fileresolve/${data}?thumbnail&width=480&height=270`
            })
    }

    async get_impl(cmd: string, timeout: number, headers: HeadersInit = {}): Promise<Response> {
        let target_machine = `${this.ip}/${cmd}`
        console.log("PROXY FETCH: ", target_machine)

        return fetch_with_timeout(target_machine, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Basic ${this.cookies}`,
                'Content-Type': 'application/json',
                ...headers
            },
        }, timeout)
    }

    async post_impl(cmd: JSON, timeout: number = 50000): Promise<Response> {
        let target_machine = `${this.ip}`
        console.log("PROXY FETCH: ", target_machine)

        return fetch_with_timeout(target_machine, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Basic ${this.cookies}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(
                cmd
            ),
        }, timeout)
    }

    async post_form_impl(cmd: string, formData: FormData, timeout: number, headers: HeadersInit = {}): Promise<Response> {
        let target_machine = `${this.ip}/${cmd}`
        console.log("PROXY FORM FETCH: ", target_machine)

        return fetch_with_timeout(target_machine, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Basic ${this.cookies}`,
                ...headers
            },
            body: formData,
        }, timeout)
    }

    async testConnection(machine: any): Promise<boolean> {
        try {
            return await this.get("ping%20version", 2500)
                .then((response: any) => {
                    if (response && response?.['Responses']?.['Ping']?.['code'] === '0') {
                        if (response?.['Responses']?.['Version']?.['code'] === '0') {
                            machine.version = response?.['Responses']?.['Version']?.['value']
                            return true
                        } else {
                            return false
                        }
                    } else {
                        return false;
                    }
                })
        } catch (err) {
            return false;
        }
    }
}

export class LocalHostConnection extends RemoteConnection {
    id: string
    constructor(machine: any) {
        super(machine);

        const port = machine.localIP.split(':')[1] ?? "";
        this.ip = "localhost:" + port;
        this.protocol = "http";
        this.id = machine['serialNumber'] ?? machine['macAddress']
    }
    type(): string { return "LocalHost"; }

    async testConnection(machine: any): Promise<boolean> {
        try {
            return await this.get("id+version", 2500)
                .then((response: any) => {
                    if (response?.['Responses']?.['ID']?.['value'] === this.id) {
                        if (response?.['Responses']?.['Version']?.['code'] === '0') {
                            machine.version = response?.['Responses']?.['Version']?.['value']
                            return true
                        } else {
                            return false;
                        }
                    } else {
                        return false;
                    }
                })
        } catch (err) {
            return false;
        }
    }

    // Checks whether a license has been checked out on the local machine.
    // A successful response looks like { Responses: { IsLicensed: { code: "0", value: "" } } }.
    // When no user is logged in the machine returns XML, which fails JSON parsing and
    // surfaces as { error } from get(), so anything other than code "0" is treated as unlicensed.
    async isLicensed(): Promise<boolean> {
        try {
            const response: any = await this.get("isLicensed", 5000);
            return response?.['Responses']?.['IsLicensed']?.['code'] === '0';
        } catch {
            return false;
        }
    }

    // Sends the user-provided API key to the local machine to check out a license.
    async setLoginInfoWithKey(loginKey: string): Promise<SpectreResponse> {
        const payload = {
            Requests: {
                SetLoginInfo: {
                    LoginKey: loginKey
                }
            }
        } as unknown as JSON;
        return this.post(payload, 15000);
    }

    // Sends the user-provided email/password to the local machine to check out a license.
    async setLoginInfoWithCredentials(email: string, password: string): Promise<SpectreResponse> {
        const payload = {
            Requests: {
                SetLoginInfo: {
                    Email: email,
                    Password: password
                }
            }
        } as unknown as JSON;
        return this.post(payload, 15000);
    }
}

export class InsecureConnection extends RemoteConnection {
    constructor(machine: any) {
        super(machine);
        this.protocol = "http";
        this.ip = machine.localIP;
    }
    type(): string { return "Insecure"; }
}

export async function get_preferred_connection(
    machine: any,
    cookies: any,
    retries = 3,
    delay = 1000
): Promise<IConnection | null> {
    for (let attempt = 0; attempt < retries; attempt++) {
        let test_connections: Array<IConnection> = [
            new LocalHostConnection(machine),
            new RemoteConnection(machine),
            new ProxyConnection(machine, cookies),
            new InsecureConnection(machine)
        ];
        let pending: Array<Promise<boolean>> = [];
        for (let connection of test_connections) {
            pending.push(connection.testConnection(machine));
        }
        const bool_array = await Promise.all(pending);
        for (let i = 0; i < bool_array.length; ++i) {
            if (bool_array[i]) return test_connections[i];
        }
        if (attempt < retries - 1) {
            await new Promise(res => setTimeout(res, delay));
        }
    }
    return null;
}

export class Machine {
    connection: IConnection;
    machineName: string;
    localIP: string;
    publicIP: string;
    settings: Settings;
    matchmakingID: number;
    location: string;
    macAddress: string;
    scanMode: number;
    certification: any;
    version: string;
    serialNumber: string;
    isOutdated?: boolean;
    isLicensed?: boolean;

    constructor(_connection: IConnection, _name: string, _localIP: string, _publicIP: string, _matchmakingID: number, _location: string, _macAddress: string, _scanMode: number, _certification: any, _version: string, _serialNumber: string, _isOutdated?: boolean) {
        this.connection = _connection;
        this.machineName = _name;
        this.localIP = _localIP;
        this.publicIP = _publicIP;
        this.settings = new Settings(this.connection)
        this.matchmakingID = _matchmakingID;
        this.location = _location;
        this.macAddress = _macAddress
        this.scanMode = _scanMode
        this.certification = _certification
        this.version = _version
        this.serialNumber = _serialNumber
        this.isOutdated = _isOutdated
    }

    async loadSettings(): Promise<void> {
        try {
            await this.settings.sync();
            this.settings.json();
            return Promise.resolve();
        } catch (error) {
            console.error("Failed to load settings:", error);
            return Promise.reject(error);
        }
    }
}

export class OfflineMachine {
    machineName: string;
    localIP: string;
    publicIP: string;
    matchmakingID: number;
    location: string;
    macAddress: string;
    scanMode: number;
    version: string;
    serialNumber: string;
    isOutdated?: boolean;

    constructor(_machineName: string, _localIP: string, _publicIP: string, _matchmakingID: number, _location: string, _macAddress: string, _scanMode: number, _version: string, _serialNumber: string, _isOutdated?: boolean) {
        this.machineName = _machineName;
        this.localIP = _localIP;
        this.publicIP = _publicIP;
        this.matchmakingID = _matchmakingID;
        this.location = _location;
        this.macAddress = _macAddress;
        this.scanMode = _scanMode;
        this.version = _version;
        this.serialNumber = _serialNumber;
        this.isOutdated = _isOutdated;
    }
}